import { createTheme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    accent: {
      main: string;
      light: string;
      dark: string;
    };
  }

  interface PaletteOptions {
    accent?: {
      main: string;
      light: string;
      dark: string;
    };
  }
}

// Gemeinsame Theme-Optionen
const getThemeOptions = (isDark: boolean): ThemeOptions => ({
  palette: {
    mode: isDark ? 'dark' : 'light',
    primary: {
      main: '#3b82f6', // Blue-500
      light: '#60a5fa', // Blue-400
      dark: '#2563eb', // Blue-600
      contrastText: '#ffffff',
    },
    secondary: {
      main: isDark ? '#94a3b8' : '#64748b', // Slate-400/500
      light: isDark ? '#cbd5e1' : '#94a3b8', // Slate-300/400
      dark: isDark ? '#64748b' : '#475569', // Slate-500/600
      contrastText: isDark ? '#0f172a' : '#ffffff',
    },
    accent: {
      main: '#10b981', // Emerald-500
      light: '#34d399', // Emerald-400
      dark: '#059669', // Emerald-600
    },
    error: {
      main: '#ef4444', // Red-500
      light: '#f87171', // Red-400
      dark: '#dc2626', // Red-600
    },
    warning: {
      main: '#f59e0b', // Amber-500
      light: '#fbbf24', // Amber-400
      dark: '#d97706', // Amber-600
    },
    success: {
      main: '#10b981', // Emerald-500
      light: '#34d399', // Emerald-400
      dark: '#059669', // Emerald-600
    },
    grey: {
      50: isDark ? '#0f172a' : '#f8fafc',
      100: isDark ? '#1e293b' : '#f1f5f9',
      200: isDark ? '#334155' : '#e2e8f0',
      300: isDark ? '#475569' : '#cbd5e1',
      400: isDark ? '#64748b' : '#94a3b8',
      500: isDark ? '#94a3b8' : '#64748b',
      600: isDark ? '#cbd5e1' : '#475569',
      700: isDark ? '#e2e8f0' : '#334155',
      800: isDark ? '#f1f5f9' : '#1e293b',
      900: isDark ? '#f8fafc' : '#0f172a',
    },
    background: {
      default: isDark ? '#0f172a' : '#ffffff',
      paper: isDark ? '#1e293b' : '#ffffff',
    },
    text: {
      primary: isDark ? '#f8fafc' : '#0f172a',
      secondary: isDark ? '#cbd5e1' : '#64748b',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.25rem', // text-4xl
      fontWeight: 800,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.875rem', // text-3xl
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem', // text-2xl
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem', // text-xl
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem', // text-lg
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem', // text-base
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem', // text-base
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem', // text-sm
      lineHeight: 1.4,
    },
    caption: {
      fontSize: '0.75rem', // text-xs
      lineHeight: 1.3,
    },
  },
  spacing: 8, // Default MUI spacing
  shape: {
    borderRadius: 6, // Rounded-md equivalent
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Disable uppercase transformation
          borderRadius: 6,
          fontSize: '0.875rem',
          fontWeight: 500,
          padding: '8px 16px',
        },
        outlined: {
          '&.Mui-disabled': {
            borderColor: isDark ? '#475569' : '#f1f5f9',
            color: isDark ? '#64748b' : '#94a3b8',
            backgroundColor: 'transparent',
          },
        },
        text: {
          color: isDark ? '#cbd5e1' : '#64748b',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(203, 213, 225, 0.08)' : 'rgba(100, 116, 139, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          boxShadow: isDark 
            ? '0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)'
            : '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: isDark 
            ? '0 1px 3px 0 rgb(0 0 0 / 0.3), 0 1px 2px -1px rgb(0 0 0 / 0.3)'
            : '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiChip: {
    },
    MuiIconButton: {
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiSelect-select': {
            color: isDark ? '#f8fafc' : '#0f172a',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            color: isDark ? '#cbd5e1' : '#64748b',
            '&.Mui-focused': {
              color: '#3b82f6',
            },
            '&.Mui-error': {
              color: '#ef4444',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
          },
        },
      },
    },
    MuiCheckbox: {
    },
    MuiRadio: {
    },
    MuiSwitch: {
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          minHeight: 48,
        },
      },
    },
  },
});

// Export functions to create themes - Updated for better dark mode contrast
export const createLightTheme = () => createTheme(getThemeOptions(false));
export const createDarkTheme = () => createTheme(getThemeOptions(true));

// Default theme (light) for backward compatibility
export const muiTheme = createLightTheme();
